import { Component, EventEmitter, Input, NO_ERRORS_SCHEMA, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DialogContentBase, DialogModule, DialogRef, KENDO_DIALOGS } from '@progress/kendo-angular-dialog';
import { EditorModule } from '@progress/kendo-angular-editor';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { LoggerService } from '../../shared/logger.service';
import { duplicateEvaluationValidator } from '../../common/utils.lib';
import { PlacementReviewStudentType, User } from '../../models/models.lib';
import { UserDataService } from '../../users/user-data.service';
import { EmailSearch } from '../../shared/components/email-search/email-search';
import { EmailSearchResult } from '../../common/obj.lib';
import {
  firstOrLastName3RequiredValidator,
  duplicateReviewStudentValidator,
  compareFieldsRequiredValidator,
  compareNoMatchValidator
} from '../../directives/casi.validators';

@Component({
  selector: 'casi-placement-review-student-add-win',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    DialogModule,
    EditorModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    DropDownsModule,
    LayoutModule,
    AutoFocusDirective,
    KENDO_DIALOGS,
    EmailSearch
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './placement-review-student-add-win.html',
  styleUrl: './placement-review-student-add-win.scss'
})
export class PlacementReviewStudentAddWin implements OnInit {

  private _student: PlacementReviewStudentType | undefined;

  @Input() title: string = 'Title Not Set';

  @Input({ required: true }) existingData: PlacementReviewStudentType[] = [];

  @Input({ required: true }) set student(val: PlacementReviewStudentType) {
    this._student = val;
    this.editForm.patchValue(this._student);
    this.primaryIsLocked = !!val.supervisor_email_found;
    this.secondaryIsLocked = !!val.additional_supervisor_email_found;
    this.hasSecondary.set(!!val.additional_supervisor_email);
  }
  get student(): PlacementReviewStudentType | undefined {
    return this._student;
  };

  private _mode: FormModes = FormModes.NOTSET;
  @Input({ required: true }) set mode(val: FormModes) {
    this._mode = val;
    this.studentIsLocked = val === FormModes.EDIT;
  }
  get mode(): FormModes {
    return this._mode;
  };

  @Output() onSubmit: EventEmitter<FormGroup> = new EventEmitter();
  @Output() onClose: EventEmitter<void> = new EventEmitter();

  editForm: FormGroup;
  formModes = FormModes;
  supervisorDs: User[] = [];
  additionalSupervisorDs: User[] = [];
  users: User[] = [];

  studentIsLocked: boolean = false;
  primaryIsLocked: boolean = false;
  secondaryIsLocked: boolean = false;
  hasSecondary = signal<boolean>(false);

  private studentValidator = compareFieldsRequiredValidator('student_first_name', 'student_last_name');
  private supervisorValidator = compareFieldsRequiredValidator('supervisor_first_name', 'supervisor_last_name');
  private additionalValidator = compareFieldsRequiredValidator('additional_supervisor_first_name', 'additional_supervisor_last_name');

  constructor(
    private fb: FormBuilder,
    private logger: LoggerService,
    private dataService: UserDataService
  ) {
    this.editForm = this.fb.group(
      {
        id: [null, []],
        placement_review_id: [null, [Validators.required]],
        student_institution_id: [null, [Validators.required]],
        student_email: [null, [Validators.required, Validators.email]],
        student_email_found: [null],
        student_first_name: [null],
        student_last_name: [null],
        student_changed: [false],
        supervisor_email: [null, [Validators.required, Validators.email]],
        supervisor_email_found: [null],
        supervisor_first_name: [null],
        supervisor_last_name: [null],
        supervisor_changed: [false],
        additional_supervisor_email: [null, [Validators.email]],
        additional_supervisor_email_found: [null],
        additional_supervisor_first_name: [null],
        additional_supervisor_last_name: [null],
        additional_supervisor_changed: [false],
        student_user_id: [null],
        supervisor_user_id: [null],
        additional_supervisor_user_id: [null],
      },
      // {
      //   validators: [
      //     // firstOrLastName4RequiredValidator
      //     firstOrLastName3RequiredValidator('supervisor_first_name', 'supervisor_last_name'),
      //     duplicateReviewStudentValidator(this.existingData, this.mode)
      //   ]
      // }
    );
  }

  //#region Form

  get f() { return this.editForm.controls; }
  get student_institution_id() { return this.editForm.get('student_institution_id'); }
  get student_email() { return this.editForm.get('student_email'); }
  get student_first_name() { return this.editForm.get('student_first_name'); }
  get student_last_name() { return this.editForm.get('student_last_name'); }
  get student_change() { return this.editForm.get('student_change'); }
  get supervisor_email() { return this.editForm.get('supervisor_email'); }
  get supervisor_email_found() { return this.editForm.get('supervisor_email_found'); }
  get supervisor_first_name() { return this.editForm.get('supervisor_first_name'); }
  get supervisor_last_name() { return this.editForm.get('supervisor_last_name'); }
  get supervisor_changed() { return this.editForm.get('supervisor_changed'); }
  get additional_supervisor_email() { return this.editForm.get('additional_supervisor_email'); }
  get additional_supervisor_email_found() { return this.editForm.get('additional_supervisor_email_found'); }
  get additional_supervisor_first_name() { return this.editForm.get('additional_supervisor_first_name'); }
  get additional_supervisor_last_name() { return this.editForm.get('additional_supervisor_last_name'); }
  get additional_supervisor_changed() { return this.editForm.get('additional_supervisor_changed'); }
  get supervisor_user_id() { return this.editForm.get('supervisor_user_id'); }
  get additional_supervisor_user_id() { return this.editForm.get('additional_supervisor_user_id'); }

  //#endregion

  //#region Ng Event handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    this.editForm.setValidators(
      [
        duplicateReviewStudentValidator(this.existingData, this.mode),
        // firstOrLastName3RequiredValidator('supervisor_first_name', 'supervisor_last_name'),
        // firstOrLastName3RequiredValidator('additional_supervisor_first_name', 'additional_supervisor_last_name'),
        compareNoMatchValidator('supervisor_email', 'additional_supervisor_email'),
        compareNoMatchValidator('student_email', 'supervisor_email'),
        compareNoMatchValidator('additional_supervisor_email', 'student_email'),
        this.studentValidator,
        this.supervisorValidator
        // compareFieldsRequiredValidator('additional_supervisor_first_name', 'additional_supervisor_last_name')
      ]
    );
    // this.editForm.updateValueAndValidity();

    this.student_email?.valueChanges.subscribe(value => {
      if (value && value !== value.toLowerCase()) {
        this.student_email?.setValue(value.toLowerCase(), { emitEvent: false });
      }
    });

    this.loadUsers();
  }

  //#endregion

  //#region Public functions

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onClose.emit();
  }

  public onSaveAction(): void {
    this.logger.debug(`onSaveAction...`, this.editForm.value);
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }
    this.onSubmit.emit(this.editForm);
  }

  public onStudentEmailSearched(evnt: EmailSearchResult): void {
    this.logger.debug(`onStudentEmailSearched: `, evnt);
    this.studentIsLocked = evnt.found;
    this.editForm.patchValue({
      student_email: evnt.searchedEmail,
      student_first_name: evnt.first_name,
      student_last_name: evnt.last_name,
      student_email_found: evnt.found,
      student_user_id: evnt.id,
      student_changed: true,
    });
  }

  public onEmailSearched(evnt: EmailSearchResult): void {
    this.logger.debug(`onEmailSearched: `, evnt);
    this.primaryIsLocked = evnt.found;
    this.editForm.patchValue({
      supervisor_email: evnt.searchedEmail,
      supervisor_first_name: evnt.first_name,
      supervisor_last_name: evnt.last_name,
      supervisor_email_found: evnt.found,
      supervisor_user_id: evnt.id,
      supervisor_changed: true
    });
  }

  public onEmailSearchedSecondary(evnt: EmailSearchResult): void {
    this.logger.debug(`onEmailSearchedSecondary: `, evnt);
    this.secondaryIsLocked = evnt.found;
    this.hasSecondary.set(!!evnt.searchedEmail);
    this.editForm.patchValue({
      additional_supervisor_email: evnt.searchedEmail,
      additional_supervisor_first_name: evnt.first_name,
      additional_supervisor_last_name: evnt.last_name,
      additional_supervisor_email_found: evnt.found,
      additional_supervisor_user_id: evnt.id,
      additional_supervisor_changed: true
    });
    if (this.hasSecondary()) {
      this.logger.debug(`onEmailSearchedSecondary:adding validator...`);
      setTimeout(() => {
        this.editForm.addValidators([this.additionalValidator]);
        this.editForm.updateValueAndValidity();
      });
    } else {
      this.logger.debug(`onEmailSearchedSecondary:removing validator...`);
      setTimeout(() => {
        this.editForm.removeValidators([this.additionalValidator]);
        this.editForm.get('additional_supervisor_last_name')!.setErrors(null);
        this.editForm.updateValueAndValidity();
        this.logger.debug(`onEmailSearchedSecondary:validators`, this.editForm.hasValidator(this.additionalValidator));
      });
    }
  }

  //#endregion

  //#region Private functions

  private updateSupervisors(supervisorIdChanged?: number, additionalSupervisorIdChanged?: number): void {
    const supervisorId = supervisorIdChanged || this.editForm.value.supervisor_user_id;
    const additionalSupervisorId = additionalSupervisorIdChanged || this.editForm.value.additional_supervisor_user_id;

    if (supervisorId === additionalSupervisorId && supervisorId != null)
      this.editForm.patchValue({ additional_supervisor_user_id: null }, { emitEvent: false });

    this.additionalSupervisorDs = this.users.filter(u => u.id !== supervisorId);
    this.supervisorDs = this.users.filter(u => u.id !== additionalSupervisorId);
  }

  private loadUsers(): void {
    this.dataService.get().subscribe({
      next: data => {
        this.users = data ? data.filter((d: User) => d.active && d.display_name) : [];
        this.supervisorDs = this.users;
        this.additionalSupervisorDs = this.users;
        this.updateSupervisors();
      },
      error: err => console.error('Failed to load users', err)
    });
  }

  //#endregion

}

